/**
 * Dr. Salus Service
 * API calls para planos, assinantes e métricas da conta Dr. Salus.
 */
import api from '../global/api';

class DrSalusService {
  // ============================================
  // PLANS
  // ============================================

  async listPlans() {
    const response = await api.get('/api/dr-salus/plans');
    return response.data;
  }

  async createPlan({ name, description, value, cycle, billing_type, charge_type = 'RECURRENT', access_days = null }) {
    const response = await api.post('/api/dr-salus/plans', {
      name, description, value, cycle, billing_type, charge_type, access_days,
    });
    return response.data;
  }

  async updatePlan(planId, updates) {
    const response = await api.put(`/api/dr-salus/plans/${planId}`, updates);
    return response.data;
  }

  // ============================================
  // SUBSCRIPTIONS
  // ============================================

  async listSubscriptions(status = null, includeEngagementOnly = false) {
    const params = {};
    if (status) params.status = status;
    if (includeEngagementOnly) params.include_engagement_only = true;
    const response = await api.get('/api/dr-salus/subscriptions', { params });
    return response.data;
  }

  async createSubscription({ plan_id, subscriber_name, subscriber_phone, subscriber_cpf, subscriber_email }) {
    const response = await api.post('/api/dr-salus/subscriptions', {
      plan_id,
      subscriber_name,
      subscriber_phone,
      subscriber_cpf,
      subscriber_email,
    });
    return response.data;
  }

  async getStats() {
    const response = await api.get('/api/dr-salus/subscriptions/stats');
    return response.data;
  }

  async grantMonths(subscriberId, months, reason = 'Concessão manual') {
    const response = await api.post(
      `/api/dr-salus/subscriptions/${subscriberId}/grant`,
      { months, reason },
    );
    return response.data;
  }

  async updateSubscription(subscriberId, updates) {
    const response = await api.put(
      `/api/dr-salus/subscriptions/${subscriberId}`,
      updates,
    );
    return response.data;
  }

  async toggleAccess(subscriberId, enabled) {
    const response = await api.patch(
      `/api/dr-salus/subscriptions/${subscriberId}/access`,
      { enabled },
    );
    return response.data;
  }

  async deleteSubscription(subscriberId) {
    const response = await api.delete(`/api/dr-salus/subscriptions/${subscriberId}`);
    return response.data;
  }

  // ============================================
  // WHITELIST
  // ============================================

  async listWhitelist() {
    const response = await api.get('/api/dr-salus/whitelist');
    return response.data;
  }

  async addToWhitelist(phone, reason = '') {
    const response = await api.post('/api/dr-salus/whitelist', { phone, reason });
    return response.data;
  }

  async toggleWhitelist(id, active) {
    const response = await api.patch(`/api/dr-salus/whitelist/${id}`, { active });
    return response.data;
  }

  async removeFromWhitelist(id) {
    const response = await api.delete(`/api/dr-salus/whitelist/${id}`);
    return response.data;
  }

  // ============================================
  // ENGAGEMENT CAP (templates não respondidos)
  // ============================================

  async getEngagementSettings() {
    const response = await api.get('/api/dr-salus/engagement/settings');
    return response.data;
  }

  async getEngagement(phone) {
    const response = await api.get(`/api/dr-salus/engagement/${encodeURIComponent(phone)}`);
    return response.data;
  }

  async updateEngagement(phone, { max_consecutive_unanswered, reengagement_template_name, clear_pause, reset_counter } = {}) {
    const payload = {};
    if (max_consecutive_unanswered !== undefined) payload.max_consecutive_unanswered = max_consecutive_unanswered;
    if (reengagement_template_name !== undefined) payload.reengagement_template_name = reengagement_template_name;
    if (clear_pause !== undefined) payload.clear_pause = clear_pause;
    if (reset_counter !== undefined) payload.reset_counter = reset_counter;
    const response = await api.put(`/api/dr-salus/engagement/${encodeURIComponent(phone)}`, payload);
    return response.data;
  }

  async resumeReminders(phone) {
    return this.updateEngagement(phone, { clear_pause: true, reset_counter: true });
  }

  // ============================================
  // DESAFIO INTESTINO BLINDADO (order bump + ativação manual)
  // ============================================

  async getDesafioConfig() {
    const response = await api.get('/api/dr-salus/desafio/config');
    return response.data;
  }

  async setDesafioProduct(productId) {
    const response = await api.put('/api/dr-salus/desafio/config', { product_id: productId || null });
    return response.data;
  }

  async getDesafioParticipants() {
    const response = await api.get('/api/dr-salus/desafio/participants');
    return response.data;
  }

  async getDesafioStatus(phone) {
    const response = await api.get(`/api/dr-salus/desafio/status/${encodeURIComponent(phone)}`);
    return response.data;
  }

  async enrollDesafio(phone, followupId = null) {
    const response = await api.post('/api/dr-salus/desafio/enroll', { phone, followup_id: followupId || null });
    return response.data;
  }

  async unenrollDesafio(phone, followupId = null) {
    const response = await api.post('/api/dr-salus/desafio/unenroll', { phone, followup_id: followupId || null });
    return response.data;
  }

  // ============================================
  // CONTROL PANEL (observabilidade agregada)
  // ============================================

  async getControlPanel({ phone = '', includeInactive = false } = {}) {
    const params = {};
    if (phone) params.phone = phone;
    if (includeInactive) params.include_inactive = true;
    const response = await api.get('/api/dr-salus/control-panel', { params });
    return response.data;
  }

  async bulkDeactivateUserReminders(phone) {
    const response = await api.post('/api/dr-salus/control-panel/bulk-deactivate', null, {
      params: { phone },
    });
    return response.data;
  }

  async findDuplicateReminders(phone = null) {
    const params = phone ? { phone } : {};
    const response = await api.get('/api/dr-salus/control-panel/duplicates', { params });
    return response.data;
  }

  async cleanDuplicateReminders({ phone = null, dryRun = false } = {}) {
    const params = {};
    if (phone) params.phone = phone;
    if (dryRun) params.dry_run = true;
    const response = await api.post('/api/dr-salus/control-panel/clean-duplicates', null, { params });
    return response.data;
  }
}

const drSalusService = new DrSalusService();
export default drSalusService;
